import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  ClaimReferralResponseSchemaType,
  GetClaimReferralResponseSchemaType,
  GetReferralStatsResponseSchemaType,
  GetReferralsResponseSchemaType,
  ReferralRequestSchemaType,
  ReferralStatsSchemaType,
} from '@/global/schemas/referral.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const referralApi = createApi({
  reducerPath: REDUX_SLICE.REFERRAL_API,
  tagTypes: ['ClaimReferralV1', 'ClaimReferralV2', 'ReferralStatsV1', 'ReferralStatsV2', 'ReferralsV1', 'ReferralsV2'],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    fetchReferrals: builder.query<
      GetReferralsResponseSchemaType,
      ReferralRequestSchemaType & { page?: number; size?: number; nodeVersion: 'v1' | 'v2' }
    >({
      query: ({ address, nft, page = 1, size = 10, nodeVersion }) => {
        if (nodeVersion === 'v1') {
          return {
            path: `v1/wallets/${address}/referrals`,
            version: 'v1',
            params: { page, size, nft },
          };
        }

        return {
          path: `v1/referrals`,
          params: { page, size, filter: `{"sale":"${nft}"}` },
          headers: {
            'x-offchain-address': address,
          },
        };
      },
      transformResponse: (returnValue: GetReferralsResponseSchemaType) => returnValue,
      providesTags: (_result, _error, { address, nft }) => [
        { type: 'ReferralsV1', id: `referral_lst_${address}_${nft}` },
      ],
    }),
    fetchReferralStats: builder.query<ReferralStatsSchemaType, ReferralRequestSchemaType & { page: string }>({
      query: ({ address, nft }) => ({
        path: `v1/wallets/${address}?nft=${nft}`,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetReferralStatsResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error, { address, nft, page }) => [
        { type: 'ReferralStatsV1', id: `referral_stats_${page}_${address}_${nft}` },
      ],
    }),
    fetchReferralStatsV2: builder.query<ReferralStatsSchemaType, ReferralRequestSchemaType & { page: string }>({
      query: ({ address, nft }) => ({
        path: `v1/projects/${nft}/referrals`,
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: GetReferralStatsResponseSchemaType) => returnValue.data,
      providesTags: (_result, _error, { address, nft, page }) => [
        { type: 'ReferralStatsV1', id: `referral_stats_${page}_${address}_${nft}` },
      ],
    }),
    claimReferralV1: builder.mutation<ClaimReferralResponseSchemaType, ReferralRequestSchemaType>({
      query: data => ({
        method: 'POST',
        path: `v1/rewards`,
        data,
        version: 'v1',
      }),
      transformResponse: (returnValue: GetClaimReferralResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'ClaimReferralV1' }],
    }),
    claimReferralV2: builder.mutation<ClaimReferralResponseSchemaType, ReferralRequestSchemaType>({
      query: ({ address, nft }) => ({
        method: 'POST',
        path: `v1/projects/${nft}/rewards`,
        headers: {
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: GetClaimReferralResponseSchemaType) => returnValue.data,
      invalidatesTags: (_result, _error) => [{ type: 'ClaimReferralV2' }],
    }),
  }),
});

export const {
  useClaimReferralV1Mutation,
  useFetchReferralsQuery,
  useFetchReferralStatsQuery,
  useFetchReferralStatsV2Query,
  useClaimReferralV2Mutation,
} = referralApi;
